/**
 * Referências citadas pelo laboratório: catálogo de produtos, modelos de
 * BLEVE e de dispersão, limiares de zona.
 *
 * Todo número com origem externa aponta para uma destas chaves. Quando o valor foi
 * derivado (uma conversão de unidade, uma razão de massas molares), a fonte é
 * `calculado` e a observação do valor diz de onde e como.
 */
export const FONTES = {
  niosh: {
    sigla: 'NIOSH',
    titulo: 'NIOSH Pocket Guide to Chemical Hazards (CDC/NIOSH)',
    url: 'https://www.cdc.gov/niosh/npg/',
  },
  aegl: {
    sigla: 'EPA AEGL',
    titulo: 'EPA — Acute Exposure Guideline Levels (AEGL), valores de 60 min',
    url: 'https://www.epa.gov/aegl',
  },
  erg2024: {
    sigla: 'GRE 2024',
    titulo: 'Emergency Response Guidebook 2024 (PHMSA/Transport Canada/SCT) — guias e Tabelas 1 e 3',
    url: 'https://cameochemicals.noaa.gov/',
  },
  adr: {
    sigla: 'ADR/ANTT',
    titulo: 'ADR / Res. ANTT 5.998/2022 — Relação de Produtos Perigosos (número de risco, classe)',
    url: 'https://unece.org/transport/standards/transport/dangerous-goods/adr-2025-agreement-concerning-international-carriage',
  },
  nist: {
    sigla: 'NIST',
    titulo: 'NIST Chemistry WebBook — propriedades de fluidos (saturação)',
    url: 'https://webbook.nist.gov/chemistry/fluid/',
  },
  engineeringToolbox: {
    sigla: 'Eng. ToolBox',
    titulo: 'Engineering ToolBox — Fuels: Higher and Lower Calorific Values (PCI)',
    url: 'https://www.engineeringtoolbox.com/fuels-higher-calorific-values-d_169.html',
  },
  cameo: {
    sigla: 'CAMEO',
    titulo: 'NOAA CAMEO Chemicals — ficha do produto',
    url: 'https://cameochemicals.noaa.gov/',
  },
  fispqDiesel: {
    sigla: 'FISPQ',
    titulo: 'FISPQ Óleo Diesel B S10 (Vibra Energia)',
    url: 'https://vibraenergia.com.br/sites/default/files/fichas-de-seguranca/oleo_diesel_b_s10.pdf',
  },
  pccCloro: {
    sigla: 'PCC',
    titulo: 'PCC Rokita — Chlorine, product information (propriedades físicas)',
    url: 'https://www.products.pcc.eu/wp-content/uploads/import/api/00786abb-94f4-4518-b7af-c85845ebeaff/chlor_broszura_en.pdf',
  },
  rempec: {
    sigla: 'REMPEC',
    titulo: 'REMPEC MIDSIS-TROCS — Methane, compressed gas',
    url: 'https://midsis.rempec.org/en/find-chemical/methane-compressed-gas',
  },
  ccps2000: {
    sigla: 'CCPS 2000',
    titulo: 'CCPS — Guidelines for Chemical Process Quantitative Risk Analysis, 2ª ed. (2000), BLEVE e bola de fogo',
    url: 'https://www.aiche.org/ccps/publications/books',
  },
  ccps2010: {
    sigla: 'CCPS 2010',
    titulo: 'CCPS — Guidelines for Vapor Cloud Explosion, Pressure Vessel Burst, BLEVE, and Flash Fire Hazards, 2ª ed. (2010)',
    url: 'https://www.aidic.it/cet/20/82/036.pdf',
  },
  tnoAmarelo: {
    sigla: 'TNO 2005',
    titulo: 'van den Bosch & Weterings (TNO) — Methods for the Calculation of Physical Effects, "Yellow Book" (CPR 14E, 2005)',
    url: 'https://www.aidic.it/cet/20/82/036.pdf',
  },
  bancoMundial1985: {
    sigla: 'World Bank 1985',
    titulo: 'World Bank (1985) — Manual of Industrial Hazard Assessment Techniques: efeitos da radiação térmica',
    url: 'https://accendoreliability.com/how-to-estimate-injury-or-potential-fatality-from-thermal-radiation-exposure/',
  },
  eisenberg1975: {
    sigla: 'Eisenberg 1975',
    titulo: 'Eisenberg, Lynch & Breeding (1975) — dose térmica q^(4/3)·t (TDU)',
    url: 'https://accendoreliability.com/how-to-estimate-injury-or-potential-fatality-from-thermal-radiation-exposure/',
  },
  alduchov1996: {
    sigla: 'Alduchov 1996',
    titulo: 'Alduchov & Eskridge (1996) — aproximação de Magnus para a pressão de saturação do vapor d’água',
    url: 'https://doi.org/10.1175/1520-0450(1996)035%3C0601:IMFAOS%3E2.0.CO;2',
  },
  epaRmp: {
    sigla: 'EPA RMP',
    titulo: '40 CFR 68.22(a) — EPA Risk Management Program: ponto final de radiação térmica de 5 kW/m² por 40 s',
    url: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-C/part-68/subpart-B/section-68.22',
  },
  briggs1973: {
    sigla: 'Briggs 1973',
    titulo: 'Briggs (1973) — coeficientes σy/σz para campo aberto e área urbana (tabela reproduzida em CCPS 2000)',
    url: 'https://www.aiche.org/ccps/publications/books',
  },
  epaIsc3: {
    sigla: 'EPA ISC3',
    titulo: 'EPA-454/B-95-003b — User’s Guide for the ISC3 Dispersion Models, vol. II, Tabelas 1-3 e 1-4 (Briggs urbano)',
    url: 'https://gaftp.epa.gov/aqmg/SCRAM/models/other/isc3/isc3v2.pdf',
  },
  turner1970: {
    sigla: 'Turner 1970',
    titulo: 'Turner (1970) — Workbook of Atmospheric Dispersion Estimates (classes de estabilidade de Pasquill)',
    url: 'https://nepis.epa.gov/',
  },
  nfpa77: {
    sigla: 'NFPA 77',
    titulo: 'NFPA 77 — Recommended Practice on Static Electricity (aterramento e equipotencialização em transferência)',
    url: 'https://www.nfpa.org/codes-and-standards/nfpa-77-standard-development/77',
  },
  apiRp2003: {
    sigla: 'API RP 2003',
    titulo: 'API RP 2003 — Protection Against Ignitions Arising Out of Static, Lightning, and Stray Currents',
    url: 'https://www.api.org/products-and-services/standards',
  },
  calculado: {
    sigla: 'calculado',
    titulo: 'Calculado no laboratório a partir de outros valores do catálogo',
    url: '',
  },
} as const

export type IdFonte = keyof typeof FONTES
